import * as React from 'react'
import { usePortalContainer } from '@/contexts/FullscreenContext'

/**
 * Where a layered Radix primitive (Popover, Select, DropdownMenu, ContextMenu,
 * HoverCard, Tooltip) should portal its content, published by whichever overlay
 * encloses it. **The NEAREST overlay wins** — React context crosses portals, so
 * a stale outer value is exactly the bug this file exists to prevent.
 *
 * ## Bug 1 — a body-portalled layer inside a Sheet is dead
 *
 * A `Sheet` is a MODAL Radix Dialog. While open it sets `pointer-events: none`
 * on `<body>` and shards the scroll lock to its own subtree, so only its own
 * descendants stay interactive. A layer portalled to `document.body` — which is
 * Radix's default, and what `usePortalContainer()` resolves to outside
 * fullscreen — is therefore a SIBLING of the dialog, not a descendant.
 *
 * It still PAINTS, because it carries its own z-index. It simply swallows every
 * click and every wheel event. That is why the failure reads as "the popover
 * opens but nothing in it works" rather than as a missing element, and why it
 * survives a screenshot review.
 *
 * Observed on the group-quotation builder: the dish picker rendered correctly
 * and neither its chips nor its scroll area responded (2026-07-29).
 *
 * So `SheetContent` publishes its own content node here.
 *
 * ## Bug 2 — a Dialog opened INSIDE a Sheet inherits the sheet's node
 *
 * `Dialog` (and the hand-rolled `Drawer`) portal to the body, so they paint
 * ABOVE the sheet. Their children, however, still read the sheet's provider
 * through the portal. A popover inside such a dialog then mounted into the
 * SHEET — a lower stacking context — and rendered BEHIND the dialog and its
 * scrim: visible, unclickable, one z-index short of useless.
 *
 * Observed on "Add agency" in the quotation builder: the phone country-code
 * list opened behind the modal (TKT-20260907-0007, 2026-09-09).
 *
 * The fix is not a bigger z-index on that one popover. Any overlay that renders
 * ABOVE the sheet must RESET this target — `LayerPortalTargetContext.Provider
 * value={null}` — so its own layers fall back to the default container, which
 * is where Radix's dismissable-layer stack already keeps them interactive (the
 * newest modal layer is the one that gets `pointer-events: auto`).
 *
 * ⛔ Do NOT make a `Dialog` publish its own content node instead: `DialogContent`
 * carries a permanent `translate-x-[-50%] translate-y-[-50%]`, and a transformed
 * ancestor becomes the containing block for `position: fixed` descendants —
 * every Radix popper (which defaults to the fixed strategy) would then be offset
 * by half the dialog. A sheet has no static transform, which is why it can.
 *
 * ⛔ Any NEW layered primitive added to `components/ui` must use
 * `useLayerPortalContainer()`, not `usePortalContainer()` directly. Any NEW
 * overlay must either publish its content node or reset the context to `null`.
 */
export const LayerPortalTargetContext = React.createContext<HTMLElement | null>(
  null
)

/** The enclosing overlay's portal node, or `null` for the default container. */
export function useLayerPortalTarget(): HTMLElement | null {
  return React.useContext(LayerPortalTargetContext)
}

/**
 * Resolved container for a layered primitive: the enclosing overlay's node when
 * there is one, else the fullscreen container, else `undefined` so Radix falls
 * back to `document.body`.
 */
export function useLayerPortalContainer(): HTMLElement | null | undefined {
  const overlayTarget = useLayerPortalTarget()
  const fullscreenContainer = usePortalContainer()
  return overlayTarget ?? fullscreenContainer
}
